import { Box, Stack, Typography } from "@mui/material";
import { QuestionnaireBlocks } from "Components/Analyses/Questionnaire/QuestionnaireBlocks";
import { useQuestionnaireContext } from "Components/Analyses/Questionnaire/QuestionnaireContext";
import { QuestionnaireInitBlock } from "Components/Analyses/Questionnaire/QuestionnaireInitBlock";
import { computeRuleState } from "Components/Analyses/Questionnaire/useRuleEngine";
import { mapAnswerPayloadToFormData } from "Components/Analyses/Questionnaire/utils";
import type { QuestionnaireAnswerItemDto } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useMemo } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";

type Props = {
  initialAnswerItems?: QuestionnaireAnswerItemDto[];
};

/**
 * The whole questionnaire on one page - the app splits the same groups into wizard steps, the PDF prints
 * them one after another. With no answers it renders the blank form for hand filling.
 */
export const PdfQuestionnaireForm = ({ initialAnswerItems = [] }: Props) => {
  const { t } = useResource();
  const { allQuestions, groups, investmentPlans } = useQuestionnaireContext();

  const defaultValues = useMemo(() => mapAnswerPayloadToFormData(initialAnswerItems), [initialAnswerItems]);
  const methods = useForm({ defaultValues, values: defaultValues });
  const formData = useWatch({ control: methods.control });
  const ruleState = useMemo(() => computeRuleState(allQuestions, {}, formData), [allQuestions, formData]);

  const visibleGroups = useMemo(() => groups.filter((group) => ruleState.isGroupVisible(group.questionnaireGroupID)), [groups, ruleState]);

  const initQuestions = useMemo(
    () => allQuestions.filter((q) => q.questionnaireGroupBlockID === null && q.parentQuestionID === null),
    [allQuestions],
  );

  return (
    <FormProvider {...methods}>
      <Stack spacing={Spacing.XXXL} pb={Spacing.XXXL}>
        <Box sx={{ breakAfter: "avoid" }}>
          <Typography variant="h2">{t(Resources.Questionnaire.Title)}</Typography>
          <Typography variant="body2" color="secondary">
            {t(Resources.Questionnaire.Subtitle)}
          </Typography>
        </Box>

        <QuestionnaireInitBlock questions={initQuestions} ruleState={ruleState} />

        {visibleGroups.map((group, index) => (
          <Stack key={group.questionnaireGroupID} spacing={Spacing.XL}>
            {/* The app shows group names in the stepper, which the PDF has no equivalent of. */}
            <Typography variant="h3" sx={{ breakAfter: "avoid" }}>{`${index + 1}. ${group.name}`}</Typography>
            <QuestionnaireBlocks group={group} questions={allQuestions} investmentPlans={investmentPlans} ruleState={ruleState} />
          </Stack>
        ))}
      </Stack>
    </FormProvider>
  );
};
